// Client-side PDF path for the tailored cover letter — GAP 2. A sibling of
// resumeToPrint.ts: SAME popup + window.print() pattern (clean print-only
// window, browser print dialog -> Save as PDF). Different from resumeToPrint
// in ONE respect: there is no existing themed DOM node to clone here (no
// CoverLetterView component with its own --tl-*-routed <style> the way
// ResumeView carries one) — the letter is plain text on the server, so this
// file builds its own tiny print-medium stylesheet from scratch, using the
// SAME print-safe near-black-on-white constants resumeToPrint.ts pins (a
// print-medium constant, not a UI theme value — the no-hardcoded-color law
// governs the APP's live surfaces, not a printed page, exactly as that file's
// own header explains).
//
// PLAIN LETTER LAYOUT (per the brief): recipient-less body text — this app
// has no hiring-manager name/address anywhere in scope to invent one from —
// just an optional applicant name line (served, never fabricated: JobDetail
// passes it from useAuth().user?.displayName, the SAME real value Settings.tsx
// and Home.tsx already render; omitted entirely when the caller has none to
// give, never a placeholder like "Candidate") above the letter body, and the
// letter text itself verbatim — nothing here reorders, summarises, or adds a
// single word to what the server composed.

const PRINT_SAFE_INK = '#1c1c1c';

/**
 * Build the print document's full HTML string. Pure (no DOM, no window) — the
 * one part of this file that's meaningfully unit-testable, split out so
 * coverLetterToPrint.test.ts can pin the escaping + the name-block
 * present/absent branches directly, independent of window.open/print.
 * `applicantName` is trimmed and omitted entirely when blank/undefined —
 * never rendered as a placeholder. `documentTitle` is additive (defaults to
 * the prior hardcoded 'Cover letter' — printResume's own default-param
 * shape, resumeToPrint.ts) — the caller typically builds it via the house
 * file-naming convention (printDocumentTitle.ts's buildPrintDocumentTitle),
 * since the browser's Save-as-PDF dialog defaults its filename to this title.
 */
export function buildCoverLetterPrintHtml(
  letterText: string,
  applicantName?: string | null,
  documentTitle = 'Cover letter',
): string {
  const trimmedName = applicantName?.trim();
  const nameBlock = trimmedName ? `<div class="cl-name">${escapeHtml(trimmedName)}</div>` : '';
  return `<!doctype html><html><head><meta charset="utf-8"><title>${escapeHtml(
    documentTitle,
  )}</title><style>${LETTER_CSS}</style></head><body><div class="cl-doc">${nameBlock}<div class="cl-body">${escapeHtml(letterText)}</div></div></body></html>`;
}

/**
 * Print the given cover letter text. Opens a print-only window with a plain
 * letter layout: an optional applicant name line, then the body verbatim
 * (whitespace preserved). Returns false if the popup was blocked — the caller
 * reports that honestly (never a silent no-op), same contract as printResume.
 */
export function printCoverLetter(
  letterText: string,
  applicantName?: string | null,
  documentTitle = 'Cover letter',
): boolean {
  const win = window.open('', '_blank', 'width=900,height=1200');
  if (!win) return false;

  win.document.open();
  win.document.write(buildCoverLetterPrintHtml(letterText, applicantName, documentTitle));
  win.document.close();

  win.focus();
  win.onafterprint = () => win.close();
  setTimeout(() => {
    win.print();
  }, 350);
  return true;
}

const LETTER_CSS = `
html, body { margin: 0; padding: 0; background: #fff; }
.cl-doc {
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Helvetica, Arial, sans-serif;
  font-size: 11pt; line-height: 1.55; color: ${PRINT_SAFE_INK};
  width: 210mm; min-height: 297mm; box-sizing: border-box;
  padding: 22mm 22mm; margin: 0 auto;
}
.cl-name { font-size: 13pt; font-weight: 700; margin-bottom: 18px; }
.cl-body { white-space: pre-wrap; overflow-wrap: anywhere; }
@media print {
  @page { size: A4; margin: 0; }
  .cl-doc { width: 100%; min-height: auto; padding: 18mm; }
}
`;

function escapeHtml(s: string): string {
  return s.replace(/[&<>"]/g, (c) =>
    c === '&' ? '&amp;' : c === '<' ? '&lt;' : c === '>' ? '&gt;' : '&quot;',
  );
}
